/** Vizuelni identitet: amblem Republike Srpske (Vlada RS) i logo Agencije za agrarna plaćanja.
 *  Podaci dolaze iz javnog API-ja (Podešavanja → Izlazni dokumenti); dok se ne učitaju koristi se ugrađeni amblem. */
import { Group, Image, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import { useT } from "./i18n/script";

export interface Branding { naziv: string; nadredjeni: string; ministarstvo: string; grb: string | null; logo: string | null }

export const AMBLEM_URL = "/branding/amblem-rs.png";
const FALLBACK: Branding = {
  naziv: "Agencija za agrarna plaćanja", nadredjeni: "Republika Srpska",
  ministarstvo: "Ministarstvo poljoprivrede, šumarstva i vodoprivrede", grb: AMBLEM_URL, logo: null,
};

export function useBranding(): Branding {
  const q = useQuery({
    queryKey: ["branding"], staleTime: 5 * 60_000, retry: 1,
    queryFn: async () => {
      const r = await fetch("/api/v1/public/branding");
      if (!r.ok) throw new Error(String(r.status));
      return (await r.json()) as Branding;
    },
  });
  return q.data ?? FALLBACK;
}

/** Amblem + (opciono) logo Agencije; visina u pikselima. */
export function BrandMarks({ h = 36, onDark = false }: { h?: number; onDark?: boolean }) {
  const b = useBranding();
  const pad = onDark ? { background: "rgba(255,255,255,.92)", borderRadius: 8, padding: 3 } : undefined;
  return (
    <Group gap={8} wrap="nowrap">
      {b.grb && <Image src={b.grb} h={h} w="auto" fit="contain" alt="Republika Srpska" />}
      {b.logo && <Image src={b.logo} h={h} w="auto" fit="contain" alt={b.naziv} style={pad} />}
    </Group>
  );
}

/** Natpis: prvi red (Republika Srpska) i naziv Agencije. */
export function BrandTitle({ color = "white", sub = "#c9e7a8", extra }: { color?: string; sub?: string; extra?: string }) {
  const { t } = useT();
  const b = useBranding();
  return (
    <Stack gap={0}>
      <Text size="xs" c={sub} lh={1.15} tt="uppercase" style={{ letterSpacing: ".05em" }}>
        {t(b.nadredjeni || "Republika Srpska")}{extra ? ` · ${extra}` : ""}
      </Text>
      <Text fw={700} c={color} lh={1.2} size="sm" truncate="end">{t(b.naziv)}</Text>
    </Stack>
  );
}
